import React, { useState } from 'react';
import { CreditCard, Download, TrendingUp, Users, HardDrive } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';

interface Invoice {
  id: string;
  date: string;
  amount: string;
  status: 'paid' | 'pending' | 'failed';
}

interface UsageMetric {
  name: string;
  used: number;
  limit: number;
  unit: string;
  icon: React.ElementType;
  color: string;
}

const mockInvoices: Invoice[] = [
  { id: 'inv_001', date: 'Oct 1, 2023', amount: '$29.00', status: 'paid' },
  { id: 'inv_002', date: 'Sep 1, 2023', amount: '$29.00', status: 'paid' },
  { id: 'inv_003', date: 'Aug 1, 2023', amount: '$29.00', status: 'paid' },
];

const mockUsage: UsageMetric[] = [
  { name: 'Storage', used: 7.5, limit: 10, unit: 'GB', icon: HardDrive, color: 'bg-blue-500' },
  { name: 'Team Members', used: 5, limit: 10, unit: 'users', icon: Users, color: 'bg-green-500' },
  { name: 'API Calls', used: 8500, limit: 10000, unit: '/mo', icon: TrendingUp, color: 'bg-purple-500' },
];

export const BillingSettings: React.FC = () => {
  const { t } = useLanguage();
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [cardDetails] = useState({
    last4: '4242',
    brand: 'Visa',
    expMonth: 12,
    expYear: 2024
  });

  const handleUpdateCard = (e: React.FormEvent) => {
    e.preventDefault();
    setIsEditingCard(false);
    // Mock update logic
    alert(t('settingsBilling.toast.cardUpdated'));
  };

  return (
    <div className="space-y-6" data-test-id="billing-settings-page">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">{t('settingsBilling.title')}</h1>
        <span className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">
          {t('settingsBilling.proPlanActive')}
        </span>
      </div>

      {/* Plan Overview */}
      <div className="bg-white shadow sm:rounded-lg overflow-hidden" data-test-id="billing-plan-overview">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('settingsBilling.currentPlan')}</h3>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div>
              <p className="text-lg font-semibold text-gray-900">{t('settingsBilling.planName')}</p>
              <p className="text-sm text-gray-500">{t('settingsBilling.planBilling')}</p>
            </div>
            <div className="mt-4 sm:mt-0 text-right">
              <p className="text-sm text-gray-500">{t('settingsBilling.nextBillingDate')}</p>
              <p className="font-medium text-gray-900">{t('settingsBilling.nextBillingDateValue')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Statistics */}
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">{t('settingsBilling.usageLimits')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockUsage.map((metric) => {
              const percentage = (metric.used / metric.limit) * 100;
              return (
                <div key={metric.name} className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-test-id={`billing-usage-item-${metric.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center mb-2">
                    <div className={cn("p-2 rounded-md mr-3 text-white", metric.color)}>
                      <metric.icon className="h-5 w-5" />
                    </div>
                  <span className="font-medium text-gray-900">{t(`settingsBilling.metrics.${metric.name}`, undefined, metric.name)}</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">{metric.used} {metric.unit}</span>
                      <span className="text-gray-500">{metric.limit} {metric.unit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={cn("h-2 rounded-full transition-all duration-500", metric.color)}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('settingsBilling.paymentMethod')}</h3>
          
          {!isEditingCard ? (
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="bg-white p-2 rounded border border-gray-200 mr-4">
                  <CreditCard className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{t('settingsBilling.visaEnding')}</p>
                  <p className="text-sm text-gray-500">{t('settingsBilling.expires')}</p>
                </div>
              </div>
              <button
                data-test-id="billing-edit-card-button"
                onClick={() => setIsEditingCard(true)}
                className="text-sm font-medium text-primary-600 hover:text-primary-900"
              >
                {t('settingsBilling.actions.update')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleUpdateCard} className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('settingsBilling.cardNumber')}</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      data-test-id="billing-card-number-input"
                      type="text"
                      className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md p-2"
                      placeholder={t('settingsBilling.placeholders.cardNumber')}
                    defaultValue={`•••• •••• •••• ${cardDetails.last4}`}
                  />
                </div>
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700">{t('settingsBilling.expiryDate')}</label>
                <input
                  data-test-id="billing-card-expiry-input"
                  type="text"
                  className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
                  placeholder={t('settingsBilling.placeholders.expiry')}
                  defaultValue={`${cardDetails.expMonth}/${String(cardDetails.expYear).slice(-2)}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('settingsBilling.cvc')}</label>
                <input
                  data-test-id="billing-card-cvc-input"
                  type="text"
                  className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md p-2"
                  placeholder={t('settingsBilling.placeholders.cvc')}
                  defaultValue=""
                />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  data-test-id="billing-cancel-edit-card-button"
                  onClick={() => setIsEditingCard(false)}
                  className="px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t('auth.common.cancel')}
                </button>
                <button
                  data-test-id="billing-save-card-button"
                  type="submit"
                  className="px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  {t('settingsBilling.actions.saveCard')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('settingsBilling.billingHistory')}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.general.table.date')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.amount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('forms.common.status')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settingsBilling.invoice')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockInvoices.map((invoice) => (
                  <tr key={invoice.id} data-test-id={`billing-invoice-row-${invoice.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.amount}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t(`settingsBilling.status.${invoice.status}`, undefined, invoice.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        data-test-id={`billing-invoice-download-${invoice.id}`}
                        className="text-primary-600 hover:text-primary-900 inline-flex items-center"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {t('settingsBilling.pdf')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
